/**
 * Pure form logic for the coaching profile editor (LG-340). Deliberately free
 * of React: null-clearing, integer validation, and the full-replace payload
 * are the rules that would silently corrupt an engagement if they drifted from
 * the server contract, so they are tested directly rather than through the DOM.
 *
 * Mirrors `profile.update`'s zod input
 * (api/_trpc/routers/admin/coaching-v2/profile.ts:33-51).
 */

export type ProfileFieldName =
  | 'goal'
  | 'targetDate'
  | 'clientElo'
  | 'optimalEloBand'
  | 'targetRuns'
  | 'targetSubmits'

/** Raw input strings. An empty string means "unset" for every nullable field. */
export type ProfileFormValues = Record<ProfileFieldName, string>

export type ProfileFormErrors = Partial<Record<ProfileFieldName, string>>

/** The slice of `OverviewEngagementDto` the editor seeds itself from. */
export interface ProfileFormSource {
  goal: string
  /** ISO string over the wire (no tRPC date transformer); Date server-side. */
  targetDate: string | Date
  clientElo: number | null
  optimalEloBand: number | null
  targetRuns: number | null
  targetSubmits: number | null
}

/**
 * The `profile.update` payload minus the identity triple. Every field is
 * present unconditionally because the mutation replaces the profile rather
 * than patching it — a field left out is a field blanked.
 */
export interface ProfileFormPayload {
  goal: string
  targetDate: Date
  clientElo: number | null
  optimalEloBand: number | null
  targetRuns: number | null
  targetSubmits: number | null
}

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/
/**
 * Digits only. Rejects '-1', '12.5', '1e3', '12abc', and '+5' in one pass —
 * all of which Number()/parseInt() would quietly accept or truncate into a
 * value the server's CHECK constraint refuses.
 */
const NONNEGATIVE_INT = /^\d+$/
/**
 * Postgres `integer` upper bound — clientElo/optimalEloBand/targetRuns/
 * targetSubmits are all `integer` columns
 * (db/schema/coaching-engagements.ts:50-51). `nonnegativeInt` has no ceiling
 * of its own server-side, so a digit string above this would otherwise reach
 * the driver and fail as a raw, un-field-scoped error.
 */
const INT4_MAX = 2147483647

/**
 * `YYYY-MM-DD` for `<input type="date">`. UTC slicing only: `targetDate` is a
 * Postgres `date` materialised at midnight UTC, so local-time getters render
 * the previous day for every viewer west of Greenwich.
 */
export function toDateInputValue(value: string | Date): string {
  if (typeof value === 'string') return value.slice(0, 10)
  if (Number.isNaN(value.getTime())) return ''
  return value.toISOString().slice(0, 10)
}

const fromNullableCount = (value: number | null): string =>
  value === null ? '' : String(value)

export function toFormValues(source: ProfileFormSource): ProfileFormValues {
  return {
    goal: source.goal,
    targetDate: toDateInputValue(source.targetDate),
    clientElo: fromNullableCount(source.clientElo),
    optimalEloBand: fromNullableCount(source.optimalEloBand),
    targetRuns: fromNullableCount(source.targetRuns),
    targetSubmits: fromNullableCount(source.targetSubmits),
  }
}

type FieldResult<T> = { ok: true; value: T } | { ok: false; error: string }

/** Mirrors `nonnegativeInt.nullable()`; blank clears to null, never to 0. */
function parseNullableCount(
  raw: string,
  label: string,
): FieldResult<number | null> {
  const trimmed = raw.trim()
  if (trimmed === '') return { ok: true, value: null }
  if (!NONNEGATIVE_INT.test(trimmed)) {
    return {
      ok: false,
      error: `${label} must be a whole number of 0 or more, or blank to clear it.`,
    }
  }
  const parsed = Number(trimmed)
  // Any digit string that clears NONNEGATIVE_INT and passes this bound is
  // automatically a safe integer too (2147483647 is far inside
  // Number.MAX_SAFE_INTEGER), so this subsumes an isSafeInteger check rather
  // than needing one alongside it.
  if (parsed > INT4_MAX) {
    return { ok: false, error: `${label} must be ${INT4_MAX} or less.` }
  }
  return { ok: true, value: parsed }
}

export type ProfileFormResult =
  | { ok: true; payload: ProfileFormPayload }
  | { ok: false; errors: ProfileFormErrors }

export function validateProfileForm(
  values: ProfileFormValues,
): ProfileFormResult {
  const errors: ProfileFormErrors = {}

  // The router trims server-side (`z.string().trim().min(1)`); trim here too
  // so the stored value matches what the coach sees after the refetch.
  const goal = values.goal.trim()
  if (goal === '') errors.goal = 'Goal is required.'

  const dateKey = values.targetDate.trim()
  let targetDate: Date | null = null
  if (!DATE_KEY.test(dateKey)) {
    errors.targetDate = 'Target date is required, as YYYY-MM-DD.'
  } else {
    const parsed = new Date(`${dateKey}T00:00:00.000Z`)
    // Round-trip check: Date rolls 2026-02-30 forward to March 2nd rather than
    // failing, so comparing back is the only way to reject impossible days.
    if (
      Number.isNaN(parsed.getTime()) ||
      parsed.toISOString().slice(0, 10) !== dateKey
    ) {
      errors.targetDate = 'Target date is not a real calendar date.'
    } else {
      targetDate = parsed
    }
  }

  const clientElo = parseNullableCount(values.clientElo, 'Client ELO')
  const optimalEloBand = parseNullableCount(
    values.optimalEloBand,
    'Optimal ELO band',
  )
  const targetRuns = parseNullableCount(values.targetRuns, 'Target runs')
  const targetSubmits = parseNullableCount(
    values.targetSubmits,
    'Target submits',
  )

  if (!clientElo.ok) errors.clientElo = clientElo.error
  if (!optimalEloBand.ok) errors.optimalEloBand = optimalEloBand.error
  if (!targetRuns.ok) errors.targetRuns = targetRuns.error
  if (!targetSubmits.ok) errors.targetSubmits = targetSubmits.error

  if (
    !clientElo.ok ||
    !optimalEloBand.ok ||
    !targetRuns.ok ||
    !targetSubmits.ok ||
    targetDate === null ||
    Object.keys(errors).length > 0
  ) {
    return { ok: false, errors }
  }

  return {
    ok: true,
    payload: {
      goal,
      targetDate,
      clientElo: clientElo.value,
      optimalEloBand: optimalEloBand.value,
      targetRuns: targetRuns.value,
      targetSubmits: targetSubmits.value,
    },
  }
}
